import { useContext, useEffect, useRef, useState } from "react";
import { AudioRecorder } from "../utils/audio-recorder";
import { useVoiceCoach } from "@/hooks/useVoiceCoach";
import { audioContext, AudioStreamer } from "@/utils/audio-streamer";

export default function VoiceCoach({
  botCustomId,
  url,
}: {
  botCustomId: string;
  url?: string;
}) {
  // const dispatch = useDispatch();
  const [firstCon, setFirstCon] = useState(true);

  const [conversationOngoing, setConversationOngoing] = useState(false);
  const [waitingForPermission, setWaitingForPermission] = useState(false);

  const [audioState, setAudioState] = useState({
    isMicOn: false,
    recorder: new AudioRecorder(),
  });

  const [screenState, setScreenState] = useState({
    isSharing: false,
    stream: null as MediaStream | null,
    isStreaming: false,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const frameTimeoutRef = useRef<number>();

  const {
    isLoading,
    isAvailable,
    response,
    connect,
    sendMediaChunk,
    error: genaAiError,
  } = useVoiceCoach({
    botId: botCustomId,
    audioStreamer: audioStreamerRef.current,
  });

  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((ctx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(ctx);
      });
    }

    return () => {
      stopEverything();
    };
  }, []);

  // Handle audio output
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioStreamerRef.current = new AudioStreamer(new AudioContext());
    }
    if (response && audioStreamerRef.current) {
      audioStreamerRef.current.addPCM16(new Uint8Array(response));
    }
  }, [response]);

  // Handle video streaming
  useEffect(() => {
    // if (!screenState.isStreaming) {
    //   // need to toggle screen share ui off, if on
    // }
    if (
      !videoRef.current ||
      !screenState.stream ||
      !screenState.isStreaming ||
      !isAvailable ||
      isLoading
    )
      return;

    videoRef.current.srcObject = screenState.stream;

    const sendFrame = () => {
      const video = videoRef.current;
      if (!video) return;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const base64 = canvas.toDataURL("image/jpeg", 0.8);
      const imageData = base64.slice(base64.indexOf(",") + 1);

      sendMediaChunk({
        mimeType: "image/jpeg",
        data: imageData,
      });

      if (screenState.isStreaming) {
        frameTimeoutRef.current = window.setTimeout(sendFrame, 2000); // 0.5 fps
      }
    };

    sendFrame();

    return () => {
      if (frameTimeoutRef.current) {
        clearTimeout(frameTimeoutRef.current);
      }
    };
  }, [screenState.isStreaming, screenState.stream, sendMediaChunk]);

  // Handle microphone permissions and setup
  const startMicrophone = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      audioState.recorder.stream = stream;
      setAudioState((prev) => ({ ...prev, isMicOn: true }));
      return true;
    } catch (err) {
      console.warn("Microphone access failed:", err);
      return false;
    }
  };

  const stopMicrophone = () => {
    try {
      audioState.recorder.stop();
      setAudioState({
        isMicOn: false,
        recorder: new AudioRecorder(),
      });
    } catch (error) {
      console.error("Error stopping microphone:", error);
    }
  };

  // Handle audio recording and streaming
  useEffect(() => {
    if (isLoading || !isAvailable) return;

    const handleAudioData = (base64: string) => {
      sendMediaChunk({
        mimeType: "audio/pcm;rate=16000",
        data: base64,
      });
    };
    const setupAudioRecording = async () => {
      if (audioState.isMicOn) {
        audioState.recorder.on("data", handleAudioData).start();
      } else {
        // stopMicrophone();
        audioState.recorder.off("data", handleAudioData).stop();
      }
    };

    setupAudioRecording();
  }, [audioState.isMicOn, audioState.recorder, sendMediaChunk]);

  useEffect(() => {
    if (genaAiError) {
      stopEverything();
      console.log(genaAiError);
    }
  }, [genaAiError]);

  const startScreenShare = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      setScreenState((prev) => ({
        ...prev,
        stream,
        isStreaming: true,
      }));
      if (url && typeof url === "string") window.open(url, "_blank")?.focus();

      // Handle stream end
      stream.getVideoTracks()[0].onended = stopScreenShare;
      setScreenState((prev) => ({ ...prev, isSharing: true }));
      return true;
    } catch (err) {
      console.warn("Screen sharing failed:", err);
      // stopEverything();
      const blobsContainer = document.querySelector(".blobs-container");
      if (blobsContainer) {
        const turnOnScreenShare = blobsContainer.querySelector(
          ".screen_share_off",
        ) as HTMLElement;
        const turnOffScreenShare = blobsContainer.querySelector(
          ".screen_share_on",
        ) as HTMLElement;
        if (turnOnScreenShare && turnOffScreenShare) {
          turnOnScreenShare.style.display = "block";
          turnOffScreenShare.style.display = "none";
        }
      }
      return false;
    }
  };

  const stopScreenShare = () => {
    try {
      if (screenState.stream) {
        screenState.stream.getTracks().forEach((track) => {
          track.stop();
        });

        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }

      setScreenState((prev) => ({
        ...prev,
        isSharing: false,
        stream: null,
        isStreaming: false,
      }));

      if (frameTimeoutRef.current) {
        clearTimeout(frameTimeoutRef.current);
      }
    } catch (error) {
      console.error("Error stopping screen share:", error);
      setScreenState({
        isSharing: false,
        stream: null,
        isStreaming: false,
      });
    }
  };

  useEffect(() => {
    if (isAvailable && firstCon) {
      setFirstCon(false);
      setConversationOngoing(true);
      setAudioState((prev) => ({ ...prev, isMicOn: true }));
      toggleUISpeechRecognition();
    }
  }, [isAvailable]);

  const toggleGenAI = async (
    localConvOnGoing: boolean = conversationOngoing,
  ) => {
    const permissionsError = "Allow Microphone access to continue";
    console.log("On Toggling GenAI", localConvOnGoing);
    try {
      if (!isLoading && !isAvailable) {
        setWaitingForPermission(true);
        // const isScreenShared = await startScreenShare();
        const isMicStarted = await startMicrophone();
        if (!isMicStarted) throw new Error(permissionsError);
        connect();
      } else if (isAvailable && localConvOnGoing) {
        stopEverything();
      } else if (isAvailable && !localConvOnGoing) {
        setConversationOngoing((prev) => !prev);
        // const isScreenShared = await startScreenShare();
        const isMicStarted = await startMicrophone();
        if (!isMicStarted) throw new Error(permissionsError);
        toggleUISpeechRecognition();
      }
    } catch (error: any) {
      console.error("Error toggling GenAI:", error);
      stopEverything();
      console.log(error.message);
    } finally {
      setWaitingForPermission(false);
    }
  };

  const removeUISpeechRecognition = () => {
    const blobsContainer = document.querySelector(
      ".blobs-container",
    ) as HTMLElement;
    const genAiOverlay = document.querySelector(
      ".gen-ai-overlay",
    ) as HTMLElement;
    if (blobsContainer) blobsContainer.remove();
    if (genAiOverlay) genAiOverlay.remove();
  };

  function toggleUISpeechRecognition() {
    const cbbBuilder = document.querySelector(
      ".preview-container-main",
    ) as HTMLElement;
    const blobsContainer = cbbBuilder.querySelector(
      ".blobs-container",
    ) as HTMLElement;
    if (!blobsContainer) {
      const blobsContainer = document.createElement("div");
      blobsContainer.className = "blobs-container absolute";
      blobsContainer.innerHTML += `<div class="blobs">
          <div class="blob_actions absolute">
              
            <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="turnOffEverything">
              <rect x="0.5" width="32" height="32" rx="16" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M23 19.4667V12.5333C23 11.7288 22.6804 10.9573 22.1116 10.3884C21.5427 9.81958 20.7712 9.5 19.9667 9.5H13.0333C12.2288 9.5 11.4573 9.81958 10.8884 10.3884C10.3196 10.9573 10 11.7288 10 12.5333V19.4667C10 20.2712 10.3196 21.0427 10.8884 21.6116C11.4573 22.1804 12.2288 22.5 13.0333 22.5H19.9667C20.7712 22.5 21.5427 22.1804 22.1116 21.6116C22.6804 21.0427 23 20.2712 23 19.4667Z" fill="#444444"/>
            </svg>
          
            <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="mic_on">
              <rect x="0.5" y="-0.000488281" width="32" height="32.0009" rx="16" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M13.7889 8.12125C13.0707 8.83949 12.6672 9.81363 12.6672 10.8294V15.0422C12.6672 16.058 13.0707 17.0321 13.7889 17.7503C14.5071 18.4686 15.4812 18.8721 16.497 18.8721C17.5127 18.8721 18.4868 18.4686 19.205 17.7503C19.9232 17.0321 20.3267 16.058 20.3267 15.0422V10.8294C20.3267 9.81363 19.9232 8.83949 19.205 8.12125C18.4868 7.40301 17.5127 6.99951 16.497 6.99951C15.4812 6.99951 14.5071 7.40301 13.7889 8.12125ZM16.4951 20.4046H16.5043C17.5719 20.4039 18.6149 20.0847 19.4999 19.4877C20.385 18.8907 21.0717 18.0431 21.4722 17.0535C21.5493 16.8665 21.6973 16.7177 21.8837 16.6393C22.0702 16.561 22.2801 16.5596 22.4675 16.6355C22.655 16.7113 22.8049 16.8583 22.8845 17.0442C22.964 17.2301 22.9668 17.44 22.8923 17.628C22.4256 18.78 21.6565 19.7846 20.6661 20.5356C19.6757 21.2865 18.5008 21.756 17.2657 21.8944V24.2345C17.2657 24.4376 17.185 24.6325 17.0413 24.7761C16.8977 24.9198 16.7029 25.0005 16.4997 25.0005C16.2966 25.0005 16.1018 24.9198 15.9581 24.7761C15.8145 24.6325 15.7338 24.4376 15.7338 24.2345V21.8944C14.4987 21.7559 13.324 21.2864 12.3338 20.5354C11.3435 19.7844 10.5745 18.7799 10.1079 17.628C10.0334 17.44 10.0362 17.2301 10.1157 17.0442C10.1953 16.8583 10.3452 16.7113 10.5327 16.6355C10.7201 16.5596 10.93 16.561 11.1165 16.6393C11.3029 16.7177 11.4509 16.8665 11.528 17.0535C11.9285 18.0429 12.6152 18.8903 13.5 19.4873C14.3849 20.0843 15.4277 20.4037 16.4951 20.4046Z" fill="#444444"/>
            </svg>
            
            <svg width="34" height="32" viewBox="0 0 34 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="mic_off">
              <rect x="0.554688" width="32.8923" height="32.0009" rx="16.0005" fill="white"/>
              <g clip-path="url(#clip0_4440_15207)">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M13.4646 9.29006C13.3146 9.66006 13.2346 10.0501 13.2146 10.4501C13.2046 10.5101 13.2046 10.5801 13.2046 10.6401V14.8601C13.2046 15.8701 13.6146 16.8501 14.3346 17.5601C15.0446 18.2801 16.0246 18.6901 17.0346 18.6901C17.2846 18.6901 17.5246 18.6701 17.7646 18.6101C18.1546 18.5401 18.5146 18.4101 18.8646 18.2201C19.1146 18.0801 19.3646 17.9101 19.5846 17.7101L13.4646 9.29006ZM19.7446 7.94006C19.0246 7.22006 18.0546 6.81006 17.0346 6.81006C16.0246 6.81006 15.0446 7.22006 14.3346 7.94006C14.2446 8.03006 14.1546 8.13006 14.0646 8.23006L20.3246 16.8201C20.6746 16.2301 20.8646 15.5501 20.8646 14.8601V10.6401C20.8646 9.63006 20.4646 8.65006 19.7446 7.94006ZM20.4946 18.9501C20.3546 19.0801 20.2046 19.1901 20.0446 19.3001C19.9546 19.3601 19.8646 19.4201 19.7746 19.4701C19.4446 19.6701 19.0946 19.8301 18.7346 19.9401C18.1946 20.1301 17.6246 20.2201 17.0446 20.2201H17.0346C15.9646 20.2201 14.9246 19.9001 14.0446 19.3001C13.1546 18.7001 12.4746 17.8601 12.0646 16.8701C11.9946 16.6801 11.8446 16.5301 11.6546 16.4501C11.4746 16.3801 11.2646 16.3701 11.0746 16.4501C10.8846 16.5301 10.7346 16.6701 10.6546 16.8601C10.5746 17.0401 10.5746 17.2501 10.6446 17.4401C11.1146 18.5901 11.8846 19.6001 12.8746 20.3501C13.8646 21.1001 15.0446 21.5701 16.2746 21.7101V24.0501C16.2746 24.2501 16.3546 24.4501 16.4946 24.5901C16.6446 24.7301 16.8346 24.8101 17.0446 24.8101C17.2446 24.8101 17.4346 24.7301 17.5846 24.5901C17.7246 24.4501 17.8046 24.2501 17.8046 24.0501V21.7101C18.4546 21.6401 19.0846 21.4701 19.6746 21.2301C20.0246 21.0801 20.3546 20.9101 20.6746 20.7101C20.8546 20.6001 21.0346 20.4801 21.2046 20.3501C21.2746 20.3001 21.3346 20.2501 21.3946 20.1901L20.4946 18.9501ZM23.4246 16.8601C23.3446 16.6701 23.1946 16.5301 23.0046 16.4501C22.8246 16.3701 22.6146 16.3801 22.4246 16.4501C22.2346 16.5301 22.0946 16.6801 22.0146 16.8701C21.8246 17.3301 21.5846 17.7601 21.2746 18.1401L22.2146 19.4201C22.7246 18.8401 23.1346 18.1701 23.4346 17.4401C23.5046 17.2501 23.5046 17.0401 23.4246 16.8601Z" fill="#444444"/>
              <path d="M13.4648 9.28996L11.3648 6.40996C11.0348 5.95996 10.4148 5.85996 9.96477 6.18996C9.51477 6.51996 9.41477 7.13996 9.74477 7.58996L13.2048 12.34L17.7648 18.61L18.7348 19.94L19.6748 21.23L22.3948 24.97C22.7248 25.42 23.3448 25.52 23.7948 25.19C24.2448 24.86 24.3448 24.24 24.0148 23.79L13.4648 9.28996Z" fill="#444444"/>
              </g>
              <defs>
              <clipPath id="clip0_4440_15207">
              <rect width="14.65" height="19.38" fill="white" transform="translate(9.55469 6)"/>
              </clipPath>
              </defs>
            </svg>

            <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="screen_share_off">
              <rect x="0.906738" y="0.000488281" width="32" height="32" rx="16" fill="white"/>
              <rect x="1.40674" y="0.500488" width="31" height="31" rx="15.5" stroke="black" stroke-opacity="0.1"/>
              <g clip-path="url(#clip0_5110_84171)">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12.9441 20.4334H10.7052C9.77796 20.4334 9.02612 19.6817 9.02612 18.7543V11.4782C9.02612 10.5509 9.77796 9.79907 10.7052 9.79907H23.0187C23.9461 9.79907 24.6978 10.5509 24.6978 11.4782V18.7543C24.6978 19.6817 23.9461 20.4334 23.0187 20.4334H20.7799V20.8696C20.7799 21.1993 20.6489 21.5154 20.4158 21.7485C20.1827 21.9816 19.8666 22.1125 19.5369 22.1125H14.187C13.8574 22.1125 13.5412 21.9816 13.3081 21.7485C13.075 21.5154 12.9441 21.1993 12.9441 20.8696V20.4334ZM14.0634 20.4334V20.8696C14.0634 20.9378 14.1188 20.9932 14.187 20.9932H19.5369C19.5697 20.9932 19.6011 20.9802 19.6243 20.957C19.6475 20.9338 19.6605 20.9024 19.6605 20.8696V20.4334H14.0634ZM16.3023 14.5086L15.6747 15.1362C15.4563 15.3546 15.1015 15.3546 14.8831 15.1362C14.6647 14.9178 14.6647 14.563 14.8831 14.3446L16.4662 12.7615C16.5712 12.6566 16.7135 12.5977 16.862 12.5977C17.0104 12.5977 17.1528 12.6566 17.2578 12.7615L18.8408 14.3446C19.0592 14.563 19.0592 14.9178 18.8408 15.1362C18.6224 15.3546 18.2678 15.3546 18.0494 15.1362L17.4216 14.5086V17.0752C17.4216 17.3842 17.1709 17.635 16.862 17.635C16.7136 17.6349 16.5713 17.5759 16.4663 17.471C16.3614 17.366 16.3024 17.2237 16.3023 17.0752V14.5086Z" fill="#444444"/>
              <rect x="22.2057" y="6.3083" width="2" height="20" transform="rotate(33.0162 22.2057 6.3083)" fill="#444444" stroke="white"/>
              </g>
              <defs>
              <clipPath id="clip0_5110_84171">
              <rect width="18" height="18" fill="white" transform="translate(7.90674 7.00049)"/>
              </clipPath>
              </defs>
            </svg>

            <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="screen_share_on">
              <rect x="0.813477" y="0.000488281" width="32" height="32" rx="16" fill="white"/>
              <rect x="1.31348" y="0.500488" width="31" height="31" rx="15.5" stroke="black" stroke-opacity="0.1"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8508 20.4334H10.612C9.6847 20.4334 8.93286 19.6817 8.93286 18.7543V11.4782C8.93286 10.5509 9.6847 9.79907 10.612 9.79907H22.9254C23.8528 9.79907 24.6045 10.5509 24.6045 11.4782V18.7543C24.6045 19.6817 23.8528 20.4334 22.9254 20.4334H20.6866V20.8696C20.6866 21.1993 20.5556 21.5154 20.3225 21.7485C20.0894 21.9816 19.7733 22.1125 19.4437 22.1125H14.0938C13.7641 22.1125 13.448 21.9816 13.2149 21.7485C12.9818 21.5154 12.8508 21.1993 12.8508 20.8696V20.4334ZM13.9702 20.4334V20.8696C13.9702 20.9378 14.0255 20.9932 14.0938 20.9932H19.4437C19.4764 20.9932 19.5079 20.9802 19.531 20.957C19.5542 20.9338 19.5672 20.9024 19.5672 20.8696V20.4334H13.9702ZM16.209 14.5086L15.5814 15.1362C15.363 15.3546 15.0083 15.3546 14.7898 15.1362C14.5714 14.9178 14.5714 14.563 14.7898 14.3446L16.3729 12.7615C16.4779 12.6566 16.6203 12.5977 16.7687 12.5977C16.9171 12.5977 17.0595 12.6566 17.1645 12.7615L18.7476 14.3446C18.966 14.563 18.966 14.9178 18.7476 15.1362C18.5291 15.3546 18.1745 15.3546 17.9561 15.1362L17.3284 14.5086V17.0752C17.3284 17.3842 17.0776 17.635 16.7688 17.635C16.6203 17.6349 16.478 17.5759 16.373 17.471C16.2681 17.366 16.2091 17.2237 16.209 17.0752V14.5086Z" fill="#444444"/>
            </svg>


          </div>
          <svg viewbox="0 0 1200 1200" class="voice-indicator" id="blobSVG">
          <defs>
              <filter id="blur1">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="10" />
              </filter>
              <filter id="blur2">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="7.5" />
              </filter>
              <filter id="blur3">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="5" />
              </filter>
              <filter id="blur4">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="100" />
              </filter>
          </defs>
          <g class="blob blob-1"><path></path></g>
          <g class="blob blob-2"><path></path></g>
          <g class="blob blob-3"><path></path></g>
          <g class="blob blob-4"><path></path></g>
          <g class="blob blob-1 alt"><path></path></g>
          <g class="blob blob-2 alt"><path></path></g>
          <g class="blob blob-3 alt"><path></path></g>
          <g class="blob blob-4 alt"><path></path></g>
      </svg>
        </div>`;
      const blobPaths = [
        "M100,600 Q0,100 600,100 T1100,600 T600,1100 T100,600 Z",
        "M100,600 Q50,200 600,100 T1050,650 T600,1100 T100,600 Z",
        "M100,600 Q0,200 600,100 T1000,600 T600,1100 T100,600 Z",
        "M150,600 Q0,0 600,100 T1100,650 T600,1100 T150,600 Z",
      ];

      const pathBlobs = blobsContainer.querySelectorAll(".blob path");

      function updatePaths() {
        pathBlobs.forEach((blob, index) => {
          const pathIndex = index % blobPaths.length;
          blob.setAttribute("d", blobPaths[pathIndex]);
        });
      }

      function animatePaths() {
        updatePaths();
        requestAnimationFrame(animatePaths);
      }

      animatePaths();
      const overlay = document.createElement("div");
      overlay.className = "gen-ai-overlay";
      cbbBuilder.appendChild(blobsContainer);
      cbbBuilder.appendChild(overlay);
      const turnOffAudio = blobsContainer.querySelector(
        ".mic_on",
      ) as HTMLElement;
      const turnOnAudio = blobsContainer.querySelector(
        ".mic_off",
      ) as HTMLElement;
      const turnOffEverything = blobsContainer.querySelector(
        ".turnOffEverything",
      ) as HTMLElement;
      const turnOffScreenShare = blobsContainer.querySelector(
        ".screen_share_on",
      ) as HTMLElement;
      const turnOnScreenShare = blobsContainer.querySelector(
        ".screen_share_off",
      ) as HTMLElement;

      turnOnScreenShare.style.display = "block";
      turnOffScreenShare.style.display = "none";
      turnOffEverything.addEventListener("click", () => {
        toggleGenAI(true);
        stopAudioAnalysisAnimation();
      });

      turnOffAudio.addEventListener("click", () => {
        setAudioState((prev) => {
          return { ...prev, isMicOn: false };
        });
        stopAudioAnalysisAnimation();
        turnOffAudio.style.display = "none";
        turnOnAudio.style.display = "block";
      });

      turnOnAudio.addEventListener("click", () => {
        setAudioState((prev) => {
          return { ...prev, isMicOn: true };
        });
        setupAudioAnalysisAnimation();
        turnOnAudio.style.display = "none";
        turnOffAudio.style.display = "block";
      });
      turnOffScreenShare?.addEventListener("click", () => {
        if (screenState.isSharing) stopScreenShare();
        else startScreenShare();
        turnOnScreenShare.style.display = "block";
        turnOffScreenShare.style.display = "none";
      });
      turnOnScreenShare?.addEventListener("click", () => {
        if (screenState.isSharing) stopScreenShare();
        else startScreenShare();
        turnOffScreenShare.style.display = "block";
        turnOnScreenShare.style.display = "none";
      });

      let analyser;
      let dataArray;
      let audioCtx;
      let source;
      let animationFrame;

      const setupAudioAnalysisAnimation = async () => {
        try {
          audioCtx = new window.AudioContext();
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          source = audioCtx.createMediaStreamSource(stream);

          analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          dataArray = new Uint8Array(analyser.frequencyBinCount);
          source.connect(analyser);
          analyzeAudioAnimation();
        } catch (error) {
          console.error("Error accessing microphone:", error);
        }
      };

      const stopAudioAnalysisAnimation = () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        if (source) {
          source.disconnect();
          source = null;
        }
        if (analyser) {
          analyser.disconnect();
          analyser = null;
        }
        if (audioCtx) {
          audioCtx.close();
          audioCtx = null;
        }
        dataArray = null;
      };

      const analyzeAudioAnimation = () => {
        analyser.getByteFrequencyData(dataArray);

        const lowFreq = getFrequencyAverage(dataArray, 0, 100);
        const scaleFactor = Math.max(1, lowFreq / 80);
        updateBlobScale(scaleFactor);

        animationFrame = requestAnimationFrame(analyzeAudioAnimation);
      };

      const getFrequencyAverage = (data, start, end) => {
        let sum = 0;
        for (let i = start; i < end; i++) {
          sum += data[i];
        }
        return sum / (end - start);
      };

      const updateBlobScale = (scaleFactor) => {
        const blobScaleWrap = blobsContainer.querySelector(
          ".voice-indicator",
        ) as SVGElement;
        if (blobScaleWrap) {
          blobScaleWrap.style.transform = `scale(${scaleFactor})`;
        }
      };

      setupAudioAnalysisAnimation();
    }
  }

  const stopEverything = () => {
    stopScreenShare();
    stopMicrophone();
    if (audioStreamerRef.current) {
      audioStreamerRef.current?.stop();
      audioStreamerRef.current = null;
    }
    if (frameTimeoutRef.current) {
      clearTimeout(frameTimeoutRef.current);
    }
    setConversationOngoing(false);
    removeUISpeechRecognition();
  };

  function fncMap(fncName: string) {
    switch (fncName) {
      case "toggle-genai":
        return toggleGenAI;
      case "turn-off-audio":
        return () => {
          setAudioState((prev) => {
            return { ...prev, isMicOn: false };
          });
        };
      case "turn-on-audio":
        return () => {
          setAudioState((prev) => {
            return { ...prev, isMicOn: true };
          });
        };
      case "toggle-screen-share":
        return () => {
          if (screenState.isSharing) stopScreenShare();
          else startScreenShare();
        };
      default:
        return () => {};
    }
  }

  useEffect(() => {
    console.log(conversationOngoing);
  }, [conversationOngoing]);

  return (
    <div className="copy-builder p-relative cp">
      <video ref={videoRef} autoPlay playsInline className="hidden" />
      {!conversationOngoing && (
        <button
          className="voice_coach_btn"
          onClick={() => toggleGenAI()}
          disabled={isLoading || waitingForPermission}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 7.014C12.1837 7.12546 10.4708 7.89727 9.18403 9.18403C7.89727 10.4708 7.12546 12.1837 7.014 14H6.986C6.87472 12.1836 6.10297 10.4706 4.81618 9.18382C3.52938 7.89703 1.8164 7.12528 0 7.014L0 6.986C1.8164 6.87472 3.52938 6.10297 4.81618 4.81618C6.10297 3.52938 6.87472 1.8164 6.986 0L7.014 0C7.12546 1.81633 7.89727 3.52921 9.18403 4.81597C10.4708 6.10273 12.1837 6.87454 14 6.986V7.014Z"
              fill="url(#paint0_radial_4472_152189)"
            />
            <defs>
              <radialGradient
                id="paint0_radial_4472_152189"
                cx="0"
                cy="0"
                r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform="translate(1.3895 5.69012) rotate(18.6832) scale(14.9013 119.368)"
              >
                <stop offset="0.067" stopColor="#9168C0" />
                <stop offset="0.343" stopColor="#5684D1" />
                <stop offset="0.672" stopColor="#1BA1E3" />
              </radialGradient>
            </defs>
          </svg>
          <span>
            {waitingForPermission
              ? "Waiting for microphone..."
              : isLoading
                ? "Initializing..."
                : "Talk To Voice Coach"}{" "}
          </span>
        </button>
      )}
    </div>
  );
}
